/**
 * @file AuthLayout.tsx
 * @description Shared chrome for unauthenticated pages — top bar with brand
 *   link + language toggle, and the page content centered on a raised
 *   `.card` surface (design-system default container). Extracted so each
 *   page stays under the 50-line max-lines-per-function rule.
 * @module Components
 * @security No user input handled here.
 */

import type { JSX, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BrandMark } from './BrandMark';
import { LanguageToggle } from './LanguageToggle';
import { PublicFooter } from './PublicFooter';

export function AuthLayout({ children }: { children: ReactNode }): JSX.Element {
  const { t } = useTranslation();
  return (
    <main className="min-h-screen flex flex-col">
      <header className="flex items-center justify-between px-4 sm:px-6 py-4">
        <Link
          to="/"
          className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <BrandMark label={t('landing.brand')} />
        </Link>
        <LanguageToggle />
      </header>
      <section className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        {/* Forms are w-full max-w-sm; the flex wrapper centers them inside
            the wider card so both locales/directions stay symmetric. */}
        <div className="card flex w-full max-w-md justify-center p-6 sm:p-8 animate-fade-up">
          {children}
        </div>
      </section>
      <PublicFooter />
    </main>
  );
}
